import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { format, formatDistanceToNow, isToday, isThisWeek } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { 
  Siren, MapPin, Clock, AlertTriangle, Filter, 
  ChevronRight, Loader2, Calendar
} from "lucide-react";
import { 
  InjurySeverity, IncidentStatus, 
  INJURY_SEVERITY_LABELS, AREA_SAFETY_LABELS 
} from "@/types/incident";
import { SiteFilter } from "./SiteFilter";

interface IncidentSignal {
  id: string;
  location: string;
  description: string | null;
  incident_datetime: string;
  injury_severity: InjurySeverity;
  area_safety_status: string;
  status: IncidentStatus;
  was_predictable: boolean;
  related_hazard_count: number;
  site?: { id: string; location_name: string } | null;
}

type FilterType = 'all' | 'still_unsafe' | 'today' | 'this_week';

const STATUS_COLORS: Record<IncidentStatus, string> = {
  reported: "bg-blue-100 text-blue-800 border-blue-200",
  investigating: "bg-purple-100 text-purple-800 border-purple-200",
  actions_pending: "bg-amber-100 text-amber-800 border-amber-200",
  verification: "bg-cyan-100 text-cyan-800 border-cyan-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS: Record<IncidentStatus, string> = {
  reported: "Dilaporkan",
  investigating: "Investigasi",
  actions_pending: "Menunggu Aksi",
  verification: "Verifikasi",
  closed: "Ditutup",
};

interface IncidentSignalsProps {
  siteId?: string | null;
}

export function IncidentSignals({ siteId: propSiteId }: IncidentSignalsProps = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [incidents, setIncidents] = useState<IncidentSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string>(propSiteId || "all");
  const [filter, setFilter] = useState<FilterType>("all");

  useEffect(() => {
    if (propSiteId) setSelectedSite(propSiteId);
  }, [propSiteId]);

  useEffect(() => {
    fetchIncidents();
  }, [selectedSite]);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("workplace_incidents")
        .select(`
          id, location, description, incident_datetime, injury_severity,
          area_safety_status, status, was_predictable, related_hazard_count,
          site:company_locations!site_id(id, location_name)
        `)
        .order("incident_datetime", { ascending: false })
        .limit(100);

      if (selectedSite !== "all") {
        query = query.eq("site_id", selectedSite);
      }

      const { data } = await query;
      setIncidents((data || []) as IncidentSignal[]);
    } catch (error) {
      console.error("Error fetching incidents:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredIncidents = incidents.filter((incident) => {
    switch (filter) {
      case "still_unsafe":
        return incident.area_safety_status === "still_unsafe";
      case "today":
        return isToday(new Date(incident.incident_datetime));
      case "this_week":
        return isThisWeek(new Date(incident.incident_datetime));
      default:
        return true;
    }
  });

  const urgentCount = incidents.filter(i => i.area_safety_status === "still_unsafe").length;
  const openCount = incidents.filter(i => i.status !== "closed").length;

  const handleIncidentClick = (incidentId: string) => {
    // Navigate to incident management tab with selected incident
    setSearchParams({ tab: "incident-management", incident: incidentId });
  };

  const getSeverityBadge = (severity: InjurySeverity) => {
    const info = INJURY_SEVERITY_LABELS[severity];
    const colorClasses: Record<InjurySeverity, string> = {
      no_injury: "bg-green-100 text-green-800",
      first_aid: "bg-yellow-100 text-yellow-800",
      medical_treatment: "bg-orange-100 text-orange-800",
      lost_time: "bg-red-100 text-red-800",
      fatality: "bg-gray-900 text-white",
    };
    return (
      <Badge className={cn("font-medium", colorClasses[severity])}>
        {info?.icon} {info?.labelId || severity}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Siren className="h-6 w-6 text-primary" />
            Incident Signals
          </h2>
          <p className="text-muted-foreground">Event intake dan visibility insiden</p>
        </div>
        
        {!propSiteId && (
          <div className="flex items-center gap-3">
            <SiteFilter value={selectedSite} onChange={setSelectedSite} />
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-primary">{incidents.length}</div>
            <div className="text-sm text-muted-foreground">Total Insiden</div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-blue-600">{openCount}</div>
            <div className="text-sm text-muted-foreground">Masih Terbuka</div>
          </CardContent>
        </Card>
        <Card className={cn("border-0 shadow-sm", urgentCount > 0 && "bg-destructive/5")}>
          <CardContent className="p-4">
            <div className={cn("text-3xl font-bold", urgentCount > 0 ? "text-destructive" : "text-muted-foreground")}>
              {urgentCount}
            </div>
            <div className="text-sm text-muted-foreground">Area Berbahaya</div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-amber-600">
              {incidents.filter(i => i.was_predictable).length}
            </div>
            <div className="text-sm text-muted-foreground">Dapat Diprediksi</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          Semua ({incidents.length})
        </Button>
        <Button
          variant={filter === "still_unsafe" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("still_unsafe")}
          className={filter === "still_unsafe" ? "" : "border-destructive/50 text-destructive hover:bg-destructive/10"}
        >
          <AlertTriangle className="h-4 w-4 mr-1" />
          Masih Berbahaya ({urgentCount})
        </Button>
        <Button
          variant={filter === "today" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("today")}
        >
          Hari Ini ({incidents.filter(i => isToday(new Date(i.incident_datetime))).length})
        </Button>
        <Button
          variant={filter === "this_week" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("this_week")}
        >
          Minggu Ini ({incidents.filter(i => isThisWeek(new Date(i.incident_datetime))).length})
        </Button>
      </div>

      {/* Incident List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Log Insiden</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredIncidents.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Tidak ada insiden ditemukan
            </div>
          ) : (
            <div className="divide-y">
              {filteredIncidents.map((incident, idx) => {
                const areaInfo = AREA_SAFETY_LABELS[incident.area_safety_status as keyof typeof AREA_SAFETY_LABELS];
                const isUrgent = incident.area_safety_status === "still_unsafe";
                
                return (
                  <motion.div
                    key={incident.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    onClick={() => handleIncidentClick(incident.id)}
                    className={cn(
                      "p-4 cursor-pointer hover:bg-muted/50 transition-colors",
                      isUrgent && "bg-destructive/5 hover:bg-destructive/10"
                    )}
                  >
                    <div className="flex items-start gap-4">
                      {/* Severity Icon */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg",
                        incident.injury_severity === "fatality" && "bg-gray-900 text-white",
                        incident.injury_severity === "lost_time" && "bg-red-100 text-red-700",
                        incident.injury_severity === "medical_treatment" && "bg-orange-100 text-orange-700",
                        incident.injury_severity === "first_aid" && "bg-yellow-100 text-yellow-700",
                        incident.injury_severity === "no_injury" && "bg-green-100 text-green-700",
                      )}>
                        {INJURY_SEVERITY_LABELS[incident.injury_severity]?.icon || "⚠️"}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {getSeverityBadge(incident.injury_severity)}
                          <Badge variant="outline" className={STATUS_COLORS[incident.status]}>
                            {STATUS_LABELS[incident.status]}
                          </Badge>
                          {isUrgent && (
                            <Badge variant="destructive" className="animate-pulse">
                              🚨 {areaInfo?.labelId}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="font-medium line-clamp-1 mb-1">
                          {incident.description || incident.location}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {incident.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(incident.incident_datetime), { 
                              addSuffix: true,
                              locale: localeId 
                            })}
                          </span>
                          {incident.site?.location_name && (
                            <span className="text-xs bg-muted px-2 py-0.5 rounded">
                              {incident.site.location_name}
                            </span>
                          )}
                        </div>

                        {incident.was_predictable && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {incident.related_hazard_count} sinyal sebelumnya terdeteksi
                          </p>
                        )}
                      </div>

                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
